import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { messageService } from '../../services/messageService';
import { useAuth } from '../../context/AuthContext.jsx';
import { 
  ArrowLeft, 
  Reply, 
  Forward, 
  Star, 
  Archive, 
  Trash2, 
  Download, 
  Paperclip,
  User,
  Clock,
  Mail,
  MailOpen,
  AlertCircle,
  Send
} from 'lucide-react';

const MessageThread = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [message, setMessage] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [replying, setReplying] = React.useState(false);
  const [replyContent, setReplyContent] = React.useState('');
  const [sendingReply, setSendingReply] = React.useState(false);

  const fetchMessage = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await messageService.getMessage(id);
      setMessage(response.data.data);
      
      // Mark as read if not already read
      if (!response.data.data.isRead) {
        await messageService.markAsRead(id);
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load message');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchMessage();
  }, [id]);

  const handleReply = async () => {
    if (!replyContent.trim()) return;

    try {
      setSendingReply(true);
      await messageService.replyToMessage(id, { content: replyContent });
      setReplyContent('');
      setReplying(false);
      alert('Reply sent successfully!');
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStar = async () => {
    try {
      await messageService.toggleStar(id);
      fetchMessage(); // Refresh message data
    } catch (error) {
      console.error('Failed to toggle star:', error);
    }
  };

  const handleArchive = async () => {
    try {
      await messageService.archiveMessage(id);
      navigate('/dashboard/messages');
    } catch (error) {
      console.error('Failed to archive message:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        await messageService.deleteMessage(id);
        navigate('/dashboard/messages');
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchMessage}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (!message) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{message.subject}</h1>
          <p className="text-gray-600 mt-1">Message Details</p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleStar}
          >
            <Star className={`w-4 h-4 mr-1 ${
              message.isStarred ? 'text-yellow-500 fill-current' : 'text-gray-400'
            }`} />
            {message.isStarred ? 'Starred' : 'Star'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleArchive}>
            <Archive className="w-4 h-4 mr-1" />
            Archive
          </Button>
          <Button variant="outline" size="sm" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Message Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Message Header */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{message.senderName}</h3>
                    <p className="text-sm text-gray-600">{message.senderEmail}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-1">
                    {message.priority && (
                      <Badge className={getPriorityColor(message.priority)}>
                        {message.priority}
                      </Badge>
                    )}
                    {message.isUrgent && (
                      <Badge variant="destructive">Urgent</Badge>
                    )}
                    {message.isImportant && (
                      <Badge variant="destructive">Important</Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 text-sm text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(message.createdAt)}</span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Message Content */}
              <div className="prose max-w-none">
                <div 
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: message.content }}
                />
              </div>

              {/* Attachments */}
              {message.attachments && message.attachments.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3 flex items-center">
                    <Paperclip className="w-4 h-4 mr-2" />
                    Attachments ({message.attachments.length})
                  </h4>
                  <div className="space-y-2">
                    {message.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Paperclip className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="font-medium">{attachment.name}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(attachment.size)}</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm">
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reply Section */}
          {replying ? (
            <Card>
              <CardHeader>
                <CardTitle>Reply to Message</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Textarea
                  placeholder="Type your reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  className="min-h-[150px]"
                />
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setReplying(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleReply}
                    disabled={sendingReply || !replyContent.trim()}
                  >
                    {sendingReply ? (
                      <>
                        <LoadingSpinner />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Reply
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex justify-center">
              <Button onClick={() => setReplying(true)}>
                <Reply className="w-4 h-4 mr-2" />
                Reply
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Message Info */}
          <Card>
            <CardHeader>
              <CardTitle>Message Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Status:</span>
                <div className="flex items-center space-x-1">
                  {message.isRead ? (
                    <>
                      <MailOpen className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600">Read</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 text-blue-500" />
                      <span className="text-sm text-blue-600">Unread</span>
                    </>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Priority:</span>
                <Badge className={getPriorityColor(message.priority)}>
                  {message.priority}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Starred:</span>
                <span className="text-sm">{message.isStarred ? 'Yes' : 'No'}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Attachments:</span>
                <span className="text-sm">{message.attachments?.length || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => setReplying(true)}
              >
                <Reply className="w-4 h-4 mr-2" />
                Reply
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => {/* Handle forward */}}
              >
                <Forward className="w-4 h-4 mr-2" />
                Forward
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleStar}
              >
                <Star className={`w-4 h-4 mr-2 ${
                  message.isStarred ? 'text-yellow-500 fill-current' : ''
                }`} />
                {message.isStarred ? 'Unstar' : 'Star'}
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleArchive}
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
            </CardContent>
          </Card>

          {/* Sender Info */}
          <Card>
            <CardHeader>
              <CardTitle>Sender Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Name:</span>
                <p className="text-sm">{message.senderName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Email:</span>
                <p className="text-sm">{message.senderEmail}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Role:</span>
                <p className="text-sm capitalize">{message.senderRole}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MessageThread;